/**
 * FirstResponseModal.jsx
 * ==========================================================================
 * 26/02/2026 v2 — Modal de saída (não de entrada).
 *
 * Aparece quando o SAC/consultor tenta **sair** de um lead em
 * ``tentativa_contacto`` sem ter registado ainda uma primeira
 * resposta (``first_contact_at`` vazio). O objectivo é garantir que
 * nenhum lead novo é abandonado sem uma anotação, mas sem obstruir
 * o utilizador enquanto lê a informação do lead.
 *
 * Três outcomes possíveis (simplificados após feedback do João Vieira):
 *
 *   1. **Falei**                        → status transita para ``contactado``.
 *   2. **Insucesso na tentativa**       → mantém ``tentativa_contacto``.
 *                                          Cobre "não atendeu", "voicemail"
 *                                          e "email/whatsapp sem resposta".
 *   3. **Reagendar**                    → mantém ``tentativa_contacto`` +
 *                                          cria follow-up agendado.
 *
 * O botão "Cancelar" fecha o modal e mantém o utilizador na página do
 * lead (não é obrigatório sair).
 * ==========================================================================
 */
import { useState } from "react";
import { toast } from "sonner";
import { PhoneCall, PhoneOff, CalendarClock, Loader2, MessageSquare, X } from "lucide-react";
import api from "@/lib/api";

const OUTCOMES = [
  { key: "falei",     label: "Falei com o cliente", hint: "Contactei o cliente e conversei — avançar para Contactado.",
    icon: PhoneCall, tone: "success" },
  { key: "insucesso", label: "Insucesso na tentativa de contacto",
    hint: "Toquei, deixei voicemail, enviei email/WhatsApp — sem sucesso.",
    icon: PhoneOff, tone: "warning" },
  { key: "reagendar", label: "Reagendar contacto",
    hint: "Não consegui falar — quero marcar novo contacto.",
    icon: CalendarClock, tone: "info" },
];

function _defaultDateTime() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TONE_CLASS = {
  success: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success-bg))] hover:border-[hsl(var(--success))] text-[hsl(var(--success))]",
  warning: "border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning-bg))] hover:border-[hsl(var(--warning))] text-[hsl(var(--warning))]",
  info:    "border-[hsl(var(--info))]/40 bg-[hsl(var(--info-bg))] hover:border-[hsl(var(--info))] text-[hsl(var(--info))]",
};

/**
 * Props
 * -----
 * @param {string}   leadId     Required.
 * @param {string}   leadName   Client name shown in the header.
 * @param {Function} onDone     Called AFTER the outcome is registered.
 *                              Parent should reload the lead + proceed
 *                              with the pending navigation.
 * @param {Function} onCancel   Called when the user clicks "Cancelar"
 *                              (stays on the lead page).
 */
export default function FirstResponseModal({ leadId, leadName, onDone, onCancel }) {
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");
  const [when, setWhen] = useState(_defaultDateTime());
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!outcome) { toast.error("Escolhe primeiro um resultado."); return; }
    if (outcome === "reagendar" && !when) {
      toast.error("Escolhe a data/hora do próximo contacto.");
      return;
    }
    setBusy(true);
    try {
      const payload = { outcome, note: note.trim() };
      if (outcome === "reagendar") {
        payload.next_followup_at = new Date(when).toISOString();
      }
      const r = await api.post(`/leads/${leadId}/first-response`, payload);
      toast.success(
        outcome === "falei"
          ? "Contactado. A sair do lead…"
          : outcome === "reagendar"
          ? "Reagendamento registado."
          : "Tentativa registada.",
      );
      onDone?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro a registar resposta.");
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="first-response-modal-backdrop"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden relative"
        data-testid="first-response-modal"
      >
        {/* Header — always-dark, uses text-white for guaranteed contrast */}
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] text-white px-6 py-5">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/60 font-semibold">Antes de sair — regista o contacto</div>
          <h2 className="text-lg font-semibold mt-1">{leadName || "Lead novo"}</h2>
          <p className="text-xs text-white/70 mt-2 leading-relaxed">
            Este lead ainda não tem qualquer registo de contacto. Antes de saíres, indica o que aconteceu — mesmo que tenha sido insucesso.
          </p>
          <button
            type="button"
            data-testid="first-response-cancel"
            onClick={() => onCancel?.()}
            aria-label="Fechar e ficar no lead"
            className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* 3 outcome buttons — stacked, since labels are longer now */}
          <div className="grid grid-cols-1 gap-2.5">
            {OUTCOMES.map((o) => {
              const Icon = o.icon;
              const active = outcome === o.key;
              return (
                <button
                  key={o.key}
                  type="button"
                  data-testid={`first-response-${o.key}`}
                  onClick={() => setOutcome(o.key)}
                  className={`text-left rounded-xl border-2 p-3 transition-colors ${TONE_CLASS[o.tone]} ${active ? "ring-2 ring-offset-1 ring-foreground/40" : ""}`}
                >
                  <div className="flex items-center gap-2 font-semibold text-sm">
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {o.label}
                  </div>
                  <div className="text-[11px] mt-1 opacity-80 leading-snug">{o.hint}</div>
                </button>
              );
            })}
          </div>

          {/* Reagendar → data/hora */}
          {outcome === "reagendar" && (
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground block mb-1.5">
                Novo contacto
              </label>
              <input
                type="datetime-local"
                data-testid="first-response-when"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm"
              />
            </div>
          )}

          {/* Optional note */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground block mb-1.5 inline-flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" /> Nota{outcome === "falei" ? " (obrigatória)" : " (opcional)"}
            </label>
            <textarea
              rows={3}
              data-testid="first-response-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={outcome === "falei"
                ? "O que o cliente pediu, situação, próximos passos…"
                : "Meios usados, respostas do cliente, próximos passos…"}
              className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              data-testid="first-response-cancel-btn"
              onClick={() => onCancel?.()}
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 bg-muted text-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-muted/70 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="button"
              data-testid="first-response-submit"
              onClick={submit}
              disabled={busy || !outcome || (outcome === "falei" && !note.trim())}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-foreground text-background rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-foreground/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Registar e sair
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
